import { DefaultTheme } from 'styled-components';
import { GRID_UNIT } from './grid/constants';
import { hexWithAlpha, rgba, swatchRange } from './palette/utils';

const offWhite: string = '#FDFDFD';
const offBlack: string = '#0A0A0A';

const primaryColor = '#1899B9';
const schedulePalette = [
  '#272640',
  '#1b3a4b',
  '#004546',
];
const deviceSizes = {
  mobileS: '320px',
  mobileM: '375px',
  mobileL: '451px',
  tablet: '768px',
  laptop: '1024px',
  laptopM: '1280px',
  laptopL: '1600px',
  desktop: '1920px',
  desktopL: '2560px',
};

const device = {
  mobileS: `(min-width: ${deviceSizes.mobileS})`,
  mobileM: `(min-width: ${deviceSizes.mobileM})`,
  mobileL: `(min-width: ${deviceSizes.mobileL})`,
  tablet: `(min-width: ${deviceSizes.tablet})`,
  laptop: `(min-width: ${deviceSizes.laptop})`,
  laptopM: `(min-width: ${deviceSizes.laptopM})`,
  laptopL: `(min-width: ${deviceSizes.laptopL})`,
  desktop: `(min-width: ${deviceSizes.desktop})`,
  desktopL: `(min-width: ${deviceSizes.desktopL})`,
};

const deviceHeight = {
  small: '(max-height: 1100)',
  large: '(min-height: 1100)',
};

const theme: DefaultTheme = {
  deviceSizes,
  device,
  deviceHeight,
  borderRadius: '2px',
  borderWidth: '1px',
  maxWidth: '1280px',
  minWidth: '1200px',
  easing: {
    transitionTiming: 'cubic-bezier(0.785, 0.135, 0.150, 0.860)',
    easeCurves: [0.785, 0.135, 0.150, 0.860],
  },
  palette: {
    schedulePalette,
    neutral: {
      ...swatchRange(offBlack),
      ...{
        white: offWhite,
        // white: offBlack,
        black: offBlack,
        transparent: hexWithAlpha(offBlack, 0.0),
      },
    },
    primary: {
      // ...swatchRange('#0E6EB5'),
      ...swatchRange(primaryColor),
      ...{ contrastText: offWhite },
    },
    secondary: {
      ...swatchRange('#e2e6d9'),
      ...{ contrastText: offBlack },
    },
    info: {
      ...swatchRange('#2196f3'),
      ...{ contrastText: offWhite },
    },
    warning: {
      ...swatchRange('#E6B000'),
      ...{ contrastText: offBlack },
    },
    success: {
      // ...swatchRange('#1da36f'),
      ...swatchRange('#7ea956'),
      // ...swatchRange('#1da337'),
      ...{ contrastText: offBlack },
    },
    error: {
      // ...swatchRange('#D1476A'),
      ...swatchRange('#f3161e'),
      ...{ contrastText: offWhite },
    },
  },
  spacing: (factor: number, unit: string = 'px'): string => `${factor * GRID_UNIT}${unit}`,
  typography: {
    body: {
      fontFamily: 'Red Hat Display',
      size: {
        large: '18px',
        medium: '18px',
        small: '16px',
        xSmall: '16px',
      },
      fontWeight: 400,
      lineHeight: 1.2,
    },
    caption: {
      fontFamily: 'Red Hat Display',
      size: {
        large: '14px',
        medium: '14px',
        small: '14px',
        xSmall: '14px',
      },
      fontWeight: 400,
      lineHeight: 1.2,
    },
    h1: {
      fontFamily: 'Red Hat Display',
      size: {
        large: '88px',
        medium: '72px',
        small: '56px',
        xSmall: '48px',
      },
      fontWeight: 600,
      lineHeight: '96%',
    },
    h2: {
      fontFamily: 'Red Hat Display',
      size: {
        large: '64px',
        medium: '48px',
        small: '44px',
        xSmall: '40px',
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    h3: {
      fontFamily: 'Red Hat Display',
      size: {
        large: '40px',
        medium: '32px',
        small: '28px',
        xSmall: '24px',
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    h4: {
      fontFamily: 'Red Hat Display',
      size: {
        large: '32px',
        medium: '28px',
        small: '24px',
        xSmall: '24px',
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    h6: {
      fontFamily: 'Red Hat Display',
      size: {
        large: '24px',
        medium: '22px',
        small: '20px',
        xSmall: '20px',
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
  },
  form: {
    strokeColor: {
      inactive: '#808080',
      active: '#020202',
    },
  },
  buttonStyle: buttonStyle,
};

export default theme;

function buttonStyle(type: 'default' | 'success' | 'primary' | 'danger' | 'ghost') {
  switch (type) {
    case 'success':
      return {
        background: theme.palette.success.hue,
        color: theme.palette.neutral.white,
        hoverBackground: theme.palette.success.D100,
        hoverColor: theme.palette.neutral.white,
        border: `2px solid ${theme.palette.success.hue}`,
        hoverBorder: `2px solid ${theme.palette.success.D100}`,
        fontWeight: 'normal',
      };
    case 'primary':
      return {
        background: theme.palette.primary.hue,
        color: theme.palette.neutral.white,
        hoverBackground: theme.palette.primary.D100,
        hoverColor: theme.palette.neutral.white,
        border: `2px solid ${theme.palette.primary.hue}`,
        hoverBorder: `2px solid ${theme.palette.primary.D100}`,
        fontWeight: 'normal',
      };
      case 'danger':
        return {
          background: theme.palette.error.hue,
          color: theme.palette.neutral.white,
          hoverBackground: theme.palette.error.D100,
          hoverColor: theme.palette.neutral.white,
          border: `2px solid ${theme.palette.error.hue}`,
          hoverBorder: `2px solid ${theme.palette.error.D100}`,
          fontWeight: 'normal',
        };
      case 'ghost':
        return {
          background: rgba(theme.palette.neutral.black, 0.05),
          border: `2px solid ${rgba(theme.palette.neutral.black, 0.05)}`,
          color: theme.palette.primary.hue,
          hoverBackground: theme.palette.primary.hue,
          hoverColor: theme.palette.neutral.white,
          fontWeight: 'bold',
      };
    case 'default':
    default:
      return {
        background: theme.palette.neutral.white,
        color: theme.palette.primary.hue,
        circleIconColor: theme.palette.primary.hue,
        hoverBackground: theme.palette.primary.hue,
        hoverColor: theme.palette.neutral.white,
        hoverBorder: `2px solid ${theme.palette.primary[500]}`,
        border: `2px solid ${theme.palette.neutral[100]}`,
        fontWeight: 'bold',
      };
  }
};